import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Eye,
  Target,
  Calendar,
  Users,
  Shield,
  CheckCircle,
  AlertCircle,
  BookOpen,
  ArrowRight,
  Briefcase,
  Clock,
  DollarSign,
  Loader2,
  TrendingUp,
} from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

interface TactiCERHModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// DONNÉES — AXES TACTICE RH (modules Hub RH)
// ============================================================================
const axesTactiCE = [
  { id: 1, nom: 'Fiche employé (Dossiers employés)', intention: 'Dossier complet par employé : profil psychométrique, documents, notes sensibles. Source unique de vérité.', etatActuel: 'En production. Utilisé activement.' },
  { id: 2, nom: 'Onboarding', intention: "Checklists d'accueil, équipements, parcours de bienvenue, suivis 90 jours. Piloté par Admin RH.", etatActuel: 'Fonctionnel. Import de l\'historique à planifier.' },
  { id: 3, nom: 'Reconnaissance, rétroaction et évaluations', intention: 'Reconnaissances, rétroactions constructives, évaluations, suivi des incidents. Cadre référentiel clair.', etatActuel: 'Fonctionnel. En attente d\'adoption par les gestionnaires.' },
  { id: 4, nom: 'Cadre de compétences', intention: 'Référentiels (piliers et compétences), évaluations ITIL 1–5. Standards d’excellence.', etatActuel: 'Fonctionnel. En attente de liaison complète aux rôles.' },
  { id: 5, nom: 'Salaires & Rémunération variable', intention: 'Salaires de base, primes, commissions, objectifs trimestriels, validations.', etatActuel: 'En production. Utilisé activement.' },
  { id: 6, nom: 'Structure organisationnelle', intention: 'Départements, rôles, codes et descriptions.', etatActuel: 'En production. Utilisé.' },
  { id: 7, nom: 'Modèles et documents', intention: 'Bibliothèque de modèles : contrats, lettres RH, politiques. Signature électronique intégrée.', etatActuel: 'Fonctionnel. En attente d\'adoption complète.' },
  { id: 8, nom: 'Audit et sécurité', intention: 'Suivi temps réel des opérations sensibles. Traçabilité complète.', etatActuel: 'Fonctionnel. En place.' },
];

// Maturité actuelle et cible (AS-IS / CIBLE)
const maturiteTactiCE = [
  { axe: 'Fiche employé', actuel: 3, cible: 5 },
  { axe: 'Onboarding', actuel: 2, cible: 4 },
  { axe: 'Rétroaction & Éval.', actuel: 2, cible: 4 },
  { axe: 'Cadre compétences', actuel: 2, cible: 4 },
  { axe: 'Salaires & Bonus', actuel: 3, cible: 5 },
  { axe: 'Structure org.', actuel: 3, cible: 4 },
  { axe: 'Modèles & docs', actuel: 2, cible: 4 },
  { axe: 'Audit & sécurité', actuel: 2, cible: 4 },
];

// RACI synthétique + détaillé
const RACI_SYNTHETIQUE = [
  { activite: 'Vision & stratégie TactiCE RH', A: 'Mélissa', R: 'Mélissa, Émilie', C: 'Dany, Dany', I: 'Eric D.' },
  { activite: 'Contenu opérationnel (données, processus)', A: 'Mélissa', R: 'Émilie, Mélissa, Gestionnaires', C: 'Dany', I: 'Dany' },
  { activite: 'Développement technique', A: 'Mélissa', R: 'Eric Durand, Dany', C: 'Mélissa', I: 'Émilie' },
  { activite: 'Cohérence PRIsME Employé', A: 'Dany', R: 'Dany', C: 'Mélissa, Émilie', I: 'Eric D.' },
  { activite: 'Standards légaux & qualité (lois RH)', A: 'Dany', R: 'Admin RH, Gestionnaires', C: 'Mélissa', I: 'Dany' },
  { activite: 'Onboarding (pilotage)', A: 'Mélissa', R: 'Admin RH', C: 'Gestionnaires', I: 'Émilie' },
];

const RACI_PAR_MODULE = [
  { module: 'Fiche employé', R: 'Gestionnaires (leur équipe)', A: 'Admin RH', C: 'Mélissa', I: 'Dany' },
  { module: 'Onboarding', R: 'Admin RH', A: 'Mélissa', C: 'Émilie', I: 'Gestionnaires' },
  { module: 'Reconnaissance & Évaluations', R: 'Gestionnaires', A: 'Mélissa', C: 'Dany', I: 'Admin RH' },
  { module: 'Cadre compétences', R: 'Admin RH, Gestionnaires', A: 'Dany', C: 'Mélissa', I: 'Dany' },
  { module: 'Salaires & Rémunération', R: 'Admin RH', A: 'Mélissa', C: 'Émilie, Dany', I: 'Dany' },
  { module: 'Structure org.', R: 'Admin RH', A: 'Mélissa', C: 'Émilie', I: 'Dany' },
  { module: 'Templates & Documents', R: 'Admin RH', A: 'Dany', C: 'Mélissa', I: 'Gestionnaires' },
  { module: 'Audit & Sécurité', R: 'Admin RH', A: 'Mélissa', C: 'Dany', I: 'Émilie' },
];

// Mapping nom → avatar + nom complet (pour tooltip)
const AVATAR_MAP: Record<string, { src: string; name: string }> = {
  'Mélissa': { src: '/MP.png', name: 'Mélissa Pelletier' },
  'Émilie': { src: '/EQ.png', name: 'Émilie Quintal' },
  'Dany': { src: '/DB.png', name: 'Dany Bourassa' },
  'Eric D.': { src: '/ED.png', name: 'Eric Durand' },
  'Eric Durand': { src: '/ED.png', name: 'Eric Durand' },
};

function RACICell({ value }: { value: string }) {
  const parts = value.split(',').map((p) => p.trim());
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {parts.map((part, i) => {
        const avatar = AVATAR_MAP[part];
        if (avatar) {
          return (
            <img
              key={i}
              src={avatar.src}
              alt={avatar.name}
              title={avatar.name}
              className="w-7 h-7 rounded-full border border-rose-400/50 object-cover cursor-help"
            />
          );
        }
        return (
          <span key={i} className="text-xs text-gray-400" title={part}>
            {part}
          </span>
        );
      })}
    </div>
  );
}

// Roadmap 2026 TactiCE RH — format bousole (effort par personne)
type TaskRH = { tache: string; r: string; a: string; heures: number };
const ROADMAP_OP_2026: { trimestre: string; theme: string; tasks: TaskRH[] }[] = [
  { trimestre: 'Q1 2026', theme: 'Fondations adoption', tasks: [
    { tache: '100 % Admin RH formés', r: 'Admin RH', a: 'Mélissa', heures: 20 },
    { tache: 'Onboarding 90j documenté', r: 'Admin RH', a: 'Mélissa', heures: 25 },
    { tache: 'Import historique (autre système)', r: 'Eric Durand, Dany', a: 'Mélissa', heures: 40 },
    { tache: 'Alertes critiques actives', r: 'Dany', a: 'Mélissa', heures: 15 },
  ]},
  { trimestre: 'Q2 2026', theme: 'Extension gestionnaires', tasks: [
    { tache: 'Gestionnaires alimentent fiches', r: 'Gestionnaires', a: 'Émilie', heures: 30 },
    { tache: 'Rétroaction structurée opérationnelle', r: 'Gestionnaires', a: 'Mélissa', heures: 25 },
    { tache: 'Cadres compétences liés aux rôles', r: 'Admin RH', a: 'Dany', heures: 20 },
  ]},
  { trimestre: 'Q3 2026', theme: 'PRIsME Employé et modèles', tasks: [
    { tache: 'Flux TactiCE → PRIsME Employé validé', r: 'Dany', a: 'Dany', heures: 35 },
    { tache: 'Bibliothèque modèles complète', r: 'Admin RH', a: 'Dany', heures: 25 },
    { tache: 'Bonus/commissions pilotés', r: 'Admin RH', a: 'Émilie', heures: 20 },
  ]},
  { trimestre: 'Q4 2026', theme: 'Adoption et vision 2027', tasks: [
    { tache: 'Tous gestionnaires actifs', r: 'Gestionnaires', a: 'Mélissa', heures: 25 },
    { tache: 'Audit annuel réalisé', r: 'Admin RH', a: 'Mélissa', heures: 15 },
    { tache: 'Roadmap 2027 TactiCE RH', r: 'Mélissa', a: 'Mélissa', heures: 20 },
  ]},
];

type UserRoiItem = {
  id: string;
  telegramId: string | null;
  name: string;
  requests: number;
  minutesSaved: number;
  hoursSaved: number;
  valueCad: number;
  lastRequestAt: string | null;
};

type UsersRoiResponse = {
  hourlyRateCad: number;
  totals: {
    users: number;
    requests: number;
    minutesSaved: number;
    hoursSaved: number;
    valueCad: number;
  };
  users: UserRoiItem[];
};

export default function TactiCERHModal({ isOpen, onClose }: TactiCERHModalProps) {
  const [activeTab, setActiveTab] = useState<'esprit' | 'gouvernance' | 'raci' | 'cadre' | 'maturite' | 'roadmap' | 'utilisateurs'>('esprit');

  const tabs = [
    { id: 'esprit' as const, label: 'Esprit & Raison', icon: Eye },
    { id: 'gouvernance' as const, label: 'Gouvernance', icon: Shield },
    { id: 'raci' as const, label: 'RACI', icon: Users },
    { id: 'cadre' as const, label: 'Cadre holistique', icon: BookOpen },
    { id: 'maturite' as const, label: 'Maturité (Spider)', icon: Target },
    { id: 'roadmap' as const, label: 'Roadmap 2026', icon: Calendar },
    { id: 'utilisateurs' as const, label: 'Utilisateurs', icon: Users },
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-slate-900 via-rose-900/30 to-slate-900 rounded-2xl w-[90vw] max-w-5xl h-[90vh] border border-rose-500/30 shadow-2xl shadow-rose-500/10 flex flex-col">
              {/* Header */}
              <div className="relative p-3 border-b border-rose-500/30 bg-gradient-to-r from-rose-900/50 to-teal-900/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 bg-gradient-to-br from-rose-500 to-teal-500 rounded-lg">
                      <Briefcase className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-teal-400">
                        TactiCE RH — Documentation holistique
                      </h2>
                      <p className="text-gray-400 text-xs">
                        Plateforme RH opérationnelle · Hub RH · Alimentation PRIsME Employé
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <img src="/MP.png" alt="Mélissa" className="w-8 h-8 rounded-full border-2 border-rose-400" title="Mélissa Pelletier - Owner" />
                      <img src="/EQ.png" alt="Émilie" className="w-8 h-8 rounded-full border-2 border-rose-400" title="Émilie - Responsable" />
                      <img src="/DB.png" alt="Dany" className="w-8 h-8 rounded-full border-2 border-rose-400" title="Dany - Approbation op. & PRIsME Employé" />
                      <img src="/ED.png" alt="Eric Durand" className="w-8 h-8 rounded-full border-2 border-rose-400" title="Eric Durand - Programmeur" />
                      <img src="/DB.png" alt="Dany" className="w-8 h-8 rounded-full border-2 border-rose-400" title="Dany - A lois RH" />
                    </div>
                  </div>
                  <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors ml-2">
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-rose-500/30 bg-slate-900/50 px-6 pt-3 pb-0 overflow-x-auto">
                <div className="flex gap-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-semibold text-sm transition-all whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'bg-gradient-to-b from-rose-600 to-teal-600 text-white shadow-lg'
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <AnimatePresence mode="wait">
                  {activeTab === 'esprit' && <EspritTab />}
                  {activeTab === 'gouvernance' && <GouvernanceTab />}
                  {activeTab === 'raci' && <RACITab />}
                  {activeTab === 'cadre' && <CadreTab />}
                  {activeTab === 'maturite' && <MaturiteTab />}
                  {activeTab === 'roadmap' && <RoadmapTab />}
                  {activeTab === 'utilisateurs' && <UtilisateursTab />}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function EspritTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-rose-600/20 via-teal-600/20 to-rose-600/20 rounded-xl p-6 border-2 border-rose-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">Esprit & Raison de TactiCE RH</h3>
        <p className="text-gray-300 text-center text-sm">
          Pourquoi TactiCE RH existe, ce qu'il fait, et comment il s'articule avec PRIsME Employé
        </p>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Eye className="w-6 h-6 text-rose-400" />
          Qu'est-ce que TactiCE RH ?
        </h4>
        <p className="text-gray-300 leading-relaxed mb-3">
          TactiCE RH est la <strong className="text-white">plateforme opérationnelle des Ressources humaines</strong> chez BZ.
        </p>
        <p className="text-gray-300 leading-relaxed mb-3">
          C'est le <strong className="text-white">Hub RH</strong> : un portail où les Admin RH et les gestionnaires accèdent aux dossiers employés, onboarding, rétroaction, cadre de compétences, salaires, rémunération variable, templates et audit. Les employés n'accèdent pas à leur dossier via TactiCE RH — ils le voient via PRIsME Employé.
        </p>
        <p className="text-gray-300 leading-relaxed">
          TactiCE RH n'est pas un simple outil de gestion. C'est la <strong className="text-white">source de vérité opérationnelle RH</strong> qui alimente l'expérience employé structurée dans PRIsME Employé.
        </p>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Target className="w-6 h-6 text-rose-400" />
          Flux TactiCE RH → PRIsME Employé
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          La cohérence entre TactiCE RH et PRIsME Employé est essentielle. Voici le flux :
        </p>
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-rose-500/10 border border-rose-500/20">
            <div className="w-10 h-10 rounded-lg bg-rose-500/30 flex items-center justify-center text-rose-300 font-bold">1</div>
            <div>
              <strong className="text-white">TactiCE RH = source des données</strong>
              <p className="text-sm text-gray-400">Fiches employés, compétences, rétroaction, onboarding, structure org. — tout est géré dans TactiCE RH.</p>
            </div>
          </div>
          <div className="flex justify-center">
            <ArrowRight className="w-6 h-6 text-rose-400" />
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-teal-500/10 border border-teal-500/20">
            <div className="w-10 h-10 rounded-lg bg-teal-500/30 flex items-center justify-center text-teal-300 font-bold">2</div>
            <div>
              <strong className="text-white">PRIsME Employé = affichage structuré par rôle</strong>
              <p className="text-sm text-gray-400">Chaque employé voit, dans PRIsME Employé, les livrables, processus et informations pertinents pour son rôle — alimentés par les données TactiCE RH.</p>
            </div>
          </div>
        </div>
        <p className="text-gray-300 text-sm mt-4">
          Dany assure l'approbation opérationnelle de la cohérence TactiCE RH ↔ PRIsME Employé.
        </p>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <CheckCircle className="w-6 h-6 text-emerald-400" />
          Pourquoi TactiCE RH existe
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
            <div><strong className="text-white">Centraliser les données RH</strong> — Fiches, documents, rétroaction, compétences en un seul endroit sécurisé.</div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
            <div><strong className="text-white">Piloter l'onboarding</strong> — Checklists, suivis 90 jours, processus documentés.</div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
            <div><strong className="text-white">Structurer rétroaction et évaluations</strong> — Cadre référentiel clair, traçabilité.</div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
            <div><strong className="text-white">Alimenter PRIsME Employé</strong> — Source unique pour l'expérience employé structurée.</div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
            <div><strong className="text-white">Respecter les lois RH</strong> — Dany (A) assure les standards de qualité et conformité.</div>
          </li>
        </ul>
      </div>

      <div className="bg-gradient-to-r from-rose-600/20 via-teal-600/20 to-rose-600/20 rounded-xl p-6 border-2 border-rose-500/30">
        <h4 className="text-lg font-bold text-white mb-3 flex items-center justify-center gap-2">
          <Target className="w-5 h-5 text-rose-400" />
          Phrase clé
        </h4>
        <p className="text-xl font-semibold text-white leading-relaxed italic text-center">
          TactiCE RH est le moteur opérationnel RH. PRIsME Employé est la vitrine structurée pour chaque employé.
        </p>
      </div>
    </div>
  );
}

function GouvernanceTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-rose-600/20 via-teal-600/20 to-rose-600/20 rounded-xl p-6 border-2 border-rose-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">Gouvernance TactiCE RH</h3>
        <p className="text-gray-300 text-center text-sm">
          Rôles, responsabilités et mécanismes de décision
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white/5 rounded-xl p-5 border border-rose-500/20">
          <h4 className="font-bold text-white mb-3 flex items-center gap-2">
            <Shield className="w-5 h-5 text-rose-400" />
            Owner
          </h4>
          <p className="text-white font-semibold">Mélissa Pelletier</p>
          <p className="text-gray-400 text-sm mt-1">Responsable globale de TactiCE RH. Décisions stratégiques, priorités, vision produit.</p>
        </div>
        <div className="bg-white/5 rounded-xl p-5 border border-rose-500/20">
          <h4 className="font-bold text-white mb-3 flex items-center gap-2">
            <Users className="w-5 h-5 text-rose-400" />
            Responsables (R)
          </h4>
          <p className="text-white font-semibold">Émilie & Mélissa</p>
          <p className="text-gray-400 text-sm mt-1">Pilotage opérationnel, contenu RH, processus. Exécution au quotidien.</p>
        </div>
        <div className="bg-white/5 rounded-xl p-5 border border-rose-500/20">
          <h4 className="font-bold text-white mb-3 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-rose-400" />
            Programmeurs
          </h4>
          <p className="text-white font-semibold">Eric Durand & Dany</p>
          <p className="text-gray-400 text-sm mt-1">Développement technique, évolution de la plateforme, intégrations.</p>
        </div>
        <div className="bg-white/5 rounded-xl p-5 border border-rose-500/20">
          <h4 className="font-bold text-white mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-teal-400" />
            Approbation opérationnelle & PRIsME Employé
          </h4>
          <p className="text-white font-semibold">Dany</p>
          <p className="text-gray-400 text-sm mt-1">Valide la cohérence TactiCE RH avec PRIsME Employé. Garant de l'alignement.</p>
        </div>
        <div className="bg-white/5 rounded-xl p-5 border border-rose-500/20 md:col-span-2">
          <h4 className="font-bold text-white mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-400" />
            Accountable (A) — Lois RH & standards
          </h4>
          <p className="text-white font-semibold">Dany</p>
          <p className="text-gray-400 text-sm mt-1">
            Responsable des standards de qualité et de contenu pour la légalité et la mécanique/méthodologie RH. Dicte les cadres et les normes.
          </p>
        </div>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
        <h4 className="font-bold text-white mb-4">Principes de gouvernance</h4>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li>• Chaque gestionnaire alimente le contenu de son équipe (fiches employés, feedback).</li>
          <li>• L'onboarding est piloté par l'Admin RH.</li>
          <li>• Dany valide les standards légaux et la qualité des contenus RH.</li>
          <li>• Dany valide la cohérence avec PRIsME Employé.</li>
        </ul>
      </div>
    </div>
  );
}

function RACITab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-rose-600/20 via-teal-600/20 to-rose-600/20 rounded-xl p-6 border-2 border-rose-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">RACI TactiCE RH</h3>
        <p className="text-gray-300 text-center text-sm">
          Synthétique et par module
        </p>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
        <h4 className="font-bold text-white mb-4">RACI synthétique</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-rose-500/30">
                <th className="text-left py-3 px-2 text-rose-300">Activité</th>
                <th className="text-left py-3 px-2 text-rose-300">A</th>
                <th className="text-left py-3 px-2 text-rose-300">R</th>
                <th className="text-left py-3 px-2 text-rose-300">C</th>
                <th className="text-left py-3 px-2 text-rose-300">I</th>
              </tr>
            </thead>
            <tbody>
              {RACI_SYNTHETIQUE.map((r, i) => (
                <tr key={i} className="border-b border-rose-500/10">
                  <td className="py-2 px-2 text-gray-300">{r.activite}</td>
                  <td className="py-2 px-2"><RACICell value={r.A} /></td>
                  <td className="py-2 px-2"><RACICell value={r.R} /></td>
                  <td className="py-2 px-2"><RACICell value={r.C} /></td>
                  <td className="py-2 px-2"><RACICell value={r.I} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
        <h4 className="font-bold text-white mb-4">RACI par module</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-rose-500/30">
                <th className="text-left py-3 px-2 text-rose-300">Module</th>
                <th className="text-left py-3 px-2 text-rose-300">R</th>
                <th className="text-left py-3 px-2 text-rose-300">A</th>
                <th className="text-left py-3 px-2 text-rose-300">C</th>
                <th className="text-left py-3 px-2 text-rose-300">I</th>
              </tr>
            </thead>
            <tbody>
              {RACI_PAR_MODULE.map((r, i) => (
                <tr key={i} className="border-b border-rose-500/10">
                  <td className="py-2 px-2 text-gray-300">{r.module}</td>
                  <td className="py-2 px-2"><RACICell value={r.R} /></td>
                  <td className="py-2 px-2"><RACICell value={r.A} /></td>
                  <td className="py-2 px-2"><RACICell value={r.C} /></td>
                  <td className="py-2 px-2"><RACICell value={r.I} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RoadmapTab() {
  // Calcul effort par personne (format bousole)
  const effortParPersonne: Record<string, number> = {};
  ROADMAP_OP_2026.forEach((q) => {
    q.tasks.forEach((t) => {
      const personnes = [...new Set([t.r, t.a].flatMap((s) => s.split(',').map((x) => x.trim())))];
      const effortParPers = t.heures / personnes.length;
      personnes.forEach((nom) => {
        effortParPersonne[nom] = (effortParPersonne[nom] || 0) + effortParPers;
      });
    });
  });
  const personnesTriees = Object.entries(effortParPersonne)
    .sort((a, b) => b[1] - a[1])
    .filter(([, e]) => e > 0);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-rose-600/20 via-teal-600/20 to-rose-600/20 rounded-xl p-6 border-2 border-rose-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">Roadmap 2026 — TactiCE RH</h3>
        <p className="text-gray-300 text-center text-sm">
          Rendre TactiCE RH vivant · Adoption et évolution
        </p>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
        <h4 className="font-bold text-amber-200 mb-2">Où en sommes-nous ?</h4>
        <p className="text-gray-300 text-sm">
          TactiCE RH est <strong className="text-white">en production</strong>. Import de l'historique d'un autre système requis. 
          <strong className="text-white"> Modules utilisés en production :</strong> Fiche employé, Salaires, Rémunération variable. 
          Tous les modules sont fonctionnels. Cette roadmap vise adoption complète et évolution en 2026.
        </p>
      </div>

      {/* Répartition effort par personne — format bousole */}
      <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl p-6 border-2 border-rose-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Target className="w-6 h-6 text-rose-400" />
          Répartition effort 2026 par personne
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {personnesTriees.map(([nom, effort]) => {
            const avatar = AVATAR_MAP[nom] || AVATAR_MAP[nom.split(' ')[0]];
            return (
              <div key={nom} className="bg-slate-900/50 rounded-lg p-3 border border-rose-500/20">
                <div className="flex items-center gap-2 mb-2">
                  {avatar ? (
                    <img src={avatar.src} alt={avatar.name} title={avatar.name} className="w-8 h-8 rounded-full border-2 border-rose-500/50 object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-rose-500/20 border-2 border-rose-500/50 flex items-center justify-center text-xs font-bold text-rose-300" title={nom}>
                      {nom.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </div>
                  )}
                  <p className="text-xs text-gray-300 truncate flex-1">{nom}</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-rose-300">{Math.round(effort)}</p>
                  <p className="text-xs text-gray-400">heures</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 pt-4 border-t border-rose-500/20">
          <p className="text-sm text-gray-400">
            <strong className="text-white">Total : {Math.round(Object.values(effortParPersonne).reduce((a, b) => a + b, 0))} heures</strong> réparties sur l'année 2026
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {ROADMAP_OP_2026.map((q) => (
          <div key={q.trimestre} className="bg-white/5 rounded-xl p-5 border border-rose-500/20">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-3 py-1 rounded-full bg-rose-500/30 text-rose-200 font-bold">{q.trimestre}</span>
              <span className="text-gray-400 text-sm">{q.theme}</span>
              <span className="text-xs text-rose-400 ml-auto">{q.tasks.reduce((s, t) => s + t.heures, 0)} h</span>
            </div>
            <ul className="space-y-2 text-gray-300 text-sm">
              {q.tasks.map((t, i) => (
                <li key={i} className="flex items-center justify-between gap-4">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-rose-400 shrink-0" />
                    {t.tache}
                  </span>
                  <span className="text-xs text-gray-500 shrink-0">R: {t.r} · A: {t.a} · {t.heures}h</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('fr-CA').format(value);
}

function formatHours(value: number) {
  return new Intl.NumberFormat('fr-CA', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  }).format(value);
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(value);
}

function UtilisateursTab() {
  const [data, setData] = useState<UsersRoiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function loadUsersRoi() {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch('/api/prisme/openclaw-agents/users-roi', { credentials: 'include' });
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || 'Erreur lors du chargement des utilisateurs');
        }
        if (active) setData(payload);
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : 'Erreur inconnue');
      } finally {
        if (active) setLoading(false);
      }
    }

    void loadUsersRoi();
    return () => {
      active = false;
    };
  }, []);

  const users = data?.users ?? [];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-rose-600/20 via-teal-600/20 to-rose-600/20 rounded-xl p-6 border-2 border-rose-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">Utilisateurs PRIsME Agent</h3>
        <p className="text-gray-300 text-center text-sm">
          Demandes, temps sauvé et valeur calculée à {data?.hourlyRateCad ?? 40}$/h
        </p>
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-3 bg-white/5 border border-rose-500/20 rounded-xl p-8 text-gray-300">
          <Loader2 className="w-5 h-5 animate-spin text-rose-300" />
          Chargement des utilisateurs...
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-200">
          {error}
        </div>
      )}

      {data && !loading && !error && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/5 rounded-xl p-5 border border-rose-500/20">
              <div className="flex items-center gap-2 text-rose-300 text-sm font-semibold mb-2">
                <Users className="w-4 h-4" />
                Utilisateurs
              </div>
              <p className="text-3xl font-bold text-white">{formatNumber(data.totals.users)}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-5 border border-rose-500/20">
              <div className="flex items-center gap-2 text-teal-300 text-sm font-semibold mb-2">
                <TrendingUp className="w-4 h-4" />
                Demandes
              </div>
              <p className="text-3xl font-bold text-white">{formatNumber(data.totals.requests)}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-5 border border-rose-500/20">
              <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold mb-2">
                <Clock className="w-4 h-4" />
                Temps sauvé
              </div>
              <p className="text-3xl font-bold text-white">{formatHours(data.totals.hoursSaved)} h</p>
            </div>
            <div className="bg-white/5 rounded-xl p-5 border border-rose-500/20">
              <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold mb-2">
                <DollarSign className="w-4 h-4" />
                Valeur
              </div>
              <p className="text-3xl font-bold text-white">{formatCurrency(data.totals.valueCad)}</p>
            </div>
          </div>

          <div className="bg-white/5 rounded-xl p-5 border border-rose-500/20">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-rose-500/30">
                    <th className="text-left py-3 px-2 text-rose-300">Utilisateur</th>
                    <th className="text-right py-3 px-2 text-rose-300">Demandes</th>
                    <th className="text-right py-3 px-2 text-rose-300">Temps sauvé</th>
                    <th className="text-right py-3 px-2 text-rose-300">Valeur 40$/h</th>
                    <th className="text-left py-3 px-2 text-rose-300">Dernière demande</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-rose-500/10 hover:bg-white/5">
                      <td className="py-3 px-2">
                        <div className="font-semibold text-white">{user.name}</div>
                        {user.telegramId && <div className="text-xs text-gray-500">Telegram {user.telegramId}</div>}
                      </td>
                      <td className="py-3 px-2 text-right text-gray-200 font-semibold">{formatNumber(user.requests)}</td>
                      <td className="py-3 px-2 text-right text-gray-300">{formatHours(user.hoursSaved)} h</td>
                      <td className="py-3 px-2 text-right text-emerald-300 font-semibold">{formatCurrency(user.valueCad)}</td>
                      <td className="py-3 px-2 text-gray-400">{user.lastRequestAt || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Échelle de maturité générique (style ITIL)
const NIVEAUX_MATURITE = [
  { niveau: 0, label: 'Inexistant', description: 'Aucun processus formalisé' },
  { niveau: 1, label: 'POC / Manuel', description: 'Quelques éléments, processus informel' },
  { niveau: 2, label: 'Documenté', description: 'Processus documenté, adoption partielle' },
  { niveau: 3, label: 'Opérationnel', description: 'Utilisé en production, processus rodé' },
  { niveau: 4, label: 'Mesuré', description: 'KPI trackés, amélioration continue' },
  { niveau: 5, label: 'Excellence', description: 'Référence, optimisé, intégré' },
];

function CadreTab() {
  const [expandedAxe, setExpandedAxe] = useState<number | null>(null);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-rose-600/20 via-teal-600/20 to-rose-600/20 rounded-xl p-6 border-2 border-rose-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">Cadre holistique TactiCE RH</h3>
        <p className="text-gray-300 text-center text-sm">
          Les 8 axes du Hub RH — Niveaux de maturité 0–5 (style ITIL)
        </p>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
        <h4 className="font-bold text-amber-200 mb-2">Où en sommes-nous ?</h4>
        <p className="text-gray-300 text-sm">
          <strong className="text-white">En production</strong> mais import de l'historique d'un autre système requis. 
          <strong className="text-white"> Modules utilisés en production :</strong> Fiche employé, Salaires, Rémunération variable. 
          Tous les modules sont fonctionnels.
        </p>
      </div>

      <div className="space-y-4">
        {axesTactiCE.map((axe) => (
          <div key={axe.id} className="bg-white/5 rounded-xl border border-rose-500/20 overflow-hidden">
            <button
              onClick={() => setExpandedAxe(expandedAxe === axe.id ? null : axe.id)}
              className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-teal-500 flex items-center justify-center text-white font-bold text-sm">
                  {axe.id}
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white text-left">{axe.nom}</h4>
                  <p className="text-rose-300/80 text-xs mt-1">{'etatActuel' in axe ? (axe as { etatActuel?: string }).etatActuel : ''}</p>
                </div>
              </div>
              <div className={`transform transition-transform ${expandedAxe === axe.id ? 'rotate-180' : ''}`}>
                <X className="w-5 h-5 text-gray-400" />
              </div>
            </button>

            {expandedAxe === axe.id && (
              <div className="p-6 border-t border-rose-500/20 space-y-4">
                <div>
                  <h5 className="text-sm font-bold text-rose-400 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Intention
                  </h5>
                  <p className="text-gray-300 text-sm leading-relaxed">{axe.intention}</p>
                </div>
                {'etatActuel' in axe && (
                  <div>
                    <h5 className="text-sm font-bold text-amber-400 mb-2">État actuel</h5>
                    <p className="text-gray-300 text-sm">{(axe as { etatActuel?: string }).etatActuel}</p>
                  </div>
                )}
                <div>
                  <h5 className="text-sm font-bold text-purple-400 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Niveaux 0 → 5 — Comment passer au niveau suivant
                  </h5>
                  <p className="text-gray-400 text-xs mb-3">
                    Chaque niveau exige des preuves visibles. Passer au suivant : documenter, former, mesurer, répéter.
                  </p>
                  <div className="space-y-2">
                    {NIVEAUX_MATURITE.map((n) => (
                      <div key={n.niveau} className="bg-slate-800/50 rounded-lg p-3 border border-rose-500/20">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-rose-400">{n.niveau}</span>
                          <span className="text-xs text-gray-500">•</span>
                          <span className="text-xs font-semibold text-purple-300">{n.label}</span>
                        </div>
                        <p className="text-gray-300 text-xs leading-relaxed">{n.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MaturiteTab() {
  const chartData = maturiteTactiCE.map((item) => ({
    category: item.axe.length > 12 ? item.axe.substring(0, 12) + '...' : item.axe,
    Actuel: (item.actuel / 5) * 100,
    Cible: (item.cible / 5) * 100,
  }));

  const actuelMoy = (maturiteTactiCE.reduce((s, i) => s + i.actuel, 0) / maturiteTactiCE.length).toFixed(1);
  const cibleMoy = (maturiteTactiCE.reduce((s, i) => s + i.cible, 0) / maturiteTactiCE.length).toFixed(1);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-rose-600/20 via-teal-600/20 to-rose-600/20 rounded-xl p-6 border-2 border-rose-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">Maturité TactiCE RH</h3>
        <p className="text-gray-300 text-center text-sm">
          Évaluation actuelle (AS-IS) et cible (CIBLE) — Niveaux 0-5 (style ITIL)
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4">
          <div className="text-sm text-yellow-300 font-medium">Score actuel (AS-IS)</div>
          <div className="text-2xl font-bold text-yellow-200">{actuelMoy}/5</div>
          <div className="text-xs text-yellow-400">État actuel</div>
        </div>
        <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-4">
          <div className="text-sm text-emerald-300 font-medium">Score cible (CIBLE)</div>
          <div className="text-2xl font-bold text-emerald-200">{cibleMoy}/5</div>
          <div className="text-xs text-emerald-400">Objectif fin 2026</div>
        </div>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
        <h4 className="text-xl font-bold text-white mb-4">Graphique radar — Maturité TactiCE RH</h4>
        <div className="h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
              <PolarGrid stroke="#4b5563" />
              <PolarAngleAxis dataKey="category" tick={{ fontSize: 11, fill: '#d1d5db' }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#9ca3af' }} tickCount={6} />
              <Radar name="Actuel (AS-IS)" dataKey="Actuel" stroke="#fbbf24" fill="#fbbf24" fillOpacity={0.2} strokeWidth={3} dot={{ fill: '#fbbf24', strokeWidth: 2, r: 4 }} />
              <Radar name="Cible (CIBLE)" dataKey="Cible" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeWidth={2} strokeDasharray="5 5" dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }} />
              <Legend wrapperStyle={{ paddingTop: '20px', color: '#d1d5db' }} iconType="circle" />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-rose-500/20">
        <h4 className="text-xl font-bold text-white mb-4">Barres — Détail par axe</h4>
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={maturiteTactiCE} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#4b5563" />
              <XAxis dataKey="axe" angle={-45} textAnchor="end" height={120} tick={{ fontSize: 10, fill: '#d1d5db' }} />
              <YAxis domain={[0, 5]} tick={{ fontSize: 10, fill: '#d1d5db' }} />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #4b5563', borderRadius: '8px' }} labelStyle={{ color: '#d1d5db' }} />
              <Legend />
              <Bar dataKey="actuel" fill="#fbbf24" name="Actuel (AS-IS)" />
              <Bar dataKey="cible" fill="#10b981" name="Cible (CIBLE)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
